import React, { useEffect, useState } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";

/**
 * Top-bar language picker for multilingual clients.
 * Uses the Google Translate Element to translate the entire page client-side.
 * The hidden #google_translate_element is required for Google's script to inject the engine,
 * but our custom dropdown UI calls the engine's hidden <select> programmatically.
 */
const LANGS = [
  { code: "en", label: "English", flag: "gb" },
  { code: "hi", label: "हिन्दी", flag: "in" },
  { code: "es", label: "Español", flag: "es" },
  { code: "fr", label: "Français", flag: "fr" },
  { code: "de", label: "Deutsch", flag: "de" },
  { code: "it", label: "Italiano", flag: "it" },
  { code: "ja", label: "日本語", flag: "jp" },
  { code: "zh-CN", label: "中文", flag: "cn" },
  { code: "ru", label: "Русский", flag: "ru" },
  { code: "ar", label: "العربية", flag: "sa" },
  { code: "pt", label: "Português", flag: "pt" },
  { code: "ko", label: "한국어", flag: "kr" },
  { code: "th", label: "ไทย", flag: "th" },
];

const flagUrl = (code) => `https://flagcdn.com/w40/${code}.png`;

function setCookie(name, value) {
  // Google Translate reads the `googtrans` cookie. Setting it forces translation on next page load.
  document.cookie = `${name}=${value};path=/`;
  // Also set on parent domain so subdomains pick it up.
  try {
    const host = window.location.hostname;
    document.cookie = `${name}=${value};path=/;domain=.${host}`;
    const parts = host.split(".");
    if (parts.length > 1) {
      const rootDomain = parts.slice(-2).join(".");
      document.cookie = `${name}=${value};path=/;domain=.${rootDomain}`;
    }
  } catch (e) {
    console.warn("[LanguageSwitcher] cookie write failed:", e?.message || e);
  }
}

export default function LanguageSwitcher({ transparent = false, menuAlign = "right" }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(() => {
    try {
      const cookie = document.cookie.split(";").find((c) => c.trim().startsWith("googtrans="));
      if (cookie) {
        const val = cookie.split("=")[1] || "";
        const code = val.replace(/^\/[a-z-]+\//, "");
        return LANGS.find((l) => l.code === code) || LANGS[0];
      }
    } catch (e) {
      console.warn("[LanguageSwitcher] cookie read failed:", e?.message || e);
    }
    return LANGS[0];
  });

  // Inject the Google Translate Element once globally
  useEffect(() => {
    if (window.__googleTranslateLoaded) return;
    window.__googleTranslateLoaded = true;

    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          includedLanguages: LANGS.map((l) => l.code).join(","),
          autoDisplay: false,
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        },
        "google_translate_element"
      );
    };

    const s = document.createElement("script");
    s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!e.target.closest("[data-testid='lang-switcher']")) setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  const pick = (lang) => {
    setCurrent(lang);
    setOpen(false);
    if (lang.code === "en") {
      // Reset to original
      setCookie("googtrans", "");
    } else {
      setCookie("googtrans", `/en/${lang.code}`);
    }
    // Reload — Google Translate reads cookie on page load.
    window.location.reload();
  };

  const baseColor = transparent
    ? "text-white hover:text-marigold border-white/30"
    : "text-foreground hover:text-saffron border-cream-300";

  return (
    <div
      className="relative notranslate"
      data-testid="lang-switcher"
      translate="no"
    >
      <button
        onClick={() => setOpen((o) => !o)}
        type="button"
        data-testid="lang-switcher-btn"
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs uppercase tracking-wider border rounded-sm transition-colors ${baseColor}`}
        aria-label="Change language"
      >
        <Globe className="w-3.5 h-3.5" />
        <img
          src={flagUrl(current.flag)}
          alt={current.code}
          className="w-5 h-3.5 object-cover rounded-[2px] hidden sm:block"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <span>{current.code.toUpperCase()}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className={`absolute top-full mt-2 w-56 max-w-[calc(100vw-2rem)] bg-white border border-cream-200 rounded-sm shadow-lg z-50 overflow-hidden animate-fade-in ${
            menuAlign === "left" ? "left-0" : "right-0"
          }`}
          data-testid="lang-switcher-menu"
        >
          <div className="px-4 py-3 border-b border-cream-100 text-[10px] uppercase tracking-[0.2em] text-saffron font-semibold">
            Choose language
          </div>
          <ul className="max-h-[60vh] overflow-y-auto">
            {LANGS.map((lang) => (
              <li key={lang.code}>
                <button
                  onClick={() => pick(lang)}
                  type="button"
                  data-testid={`lang-option-${lang.code}`}
                  className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-cream-100 transition-colors ${
                    current.code === lang.code ? "text-saffron font-semibold bg-cream-50" : "text-foreground"
                  }`}
                >
                  <img
                    src={flagUrl(lang.flag)}
                    alt={lang.code}
                    className="w-6 h-4 object-cover rounded-[2px] flex-shrink-0"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <span className="flex-1">{lang.label}</span>
                  {current.code === lang.code && <Check className="w-4 h-4 text-saffron" />}
                </button>
              </li>
            ))}
          </ul>
          <div className="px-4 py-2 border-t border-cream-100 text-[10px] text-foreground/55">
            Translations powered by Google
          </div>
        </div>
      )}

      {/* Hidden Google Translate element required by Google's script */}
      <div id="google_translate_element" style={{ display: "none" }} />
    </div>
  );
}
